import React, { useState, useEffect } from "react";
import {
  Box, Typography, TextField, Button, Autocomplete,
  CircularProgress, Grid, Paper,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useGetDocRepMutation, useSubmitWeeklyMutation } from "state/api";
import CameraUpload from "components/CameraUpload";
import { toast } from "react-toastify";
import bisberryLogo from "../../assets/Bisberry.png";

const BisberryLogo = () => (
  <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
    <Box
      component="img"
      src={bisberryLogo}
      alt="Bisberry Weekly Performance Tracker"
      sx={{ maxHeight: 110, width: "auto", objectFit: "contain" }}
    />
  </Box>
);

/* ── Week helpers ── */
const getMondayOf = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00");
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d.toISOString().split("T")[0];
};

const weekRangeLabel = (dateStr) => {
  const mon = getMondayOf(dateStr);
  if (!mon) return "";
  const monD = new Date(mon + "T00:00:00");
  const sunD = new Date(monD);
  sunD.setDate(sunD.getDate() + 6);
  const DAY = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const fmt = (d) => `${DAY[d.getDay()]} ${d.getDate()}/${d.getMonth() + 1}`;
  return `${fmt(monD)} – ${fmt(sunD)}`;
};

const emptyPrescriber = { doctorName: "", doctCd: "" };

const WeeklyTracker = () => {
  const { user } = useSelector((s) => s.auth);
  const [getDocRep] = useGetDocRepMutation();
  const [submitWeekly, { isLoading }] = useSubmitWeeklyMutation();

  const [doctors, setDoctors] = useState([]);

  /* 4 week dates */
  const today = new Date().toISOString().split("T")[0];
  const [weekDates, setWeekDates] = useState([today, "", "", ""]);

  const [chemistStockCount, setChemistStockCount] = useState("");
  const [totalStripsAtChemist, setTotalStripsAtChemist] = useState("");
  const [pobTakenStrips, setPobTakenStrips] = useState("");
  const [prescriberCount, setPrescriberCount] = useState(1);
  const [prescribers, setPrescribers] = useState([{ ...emptyPrescriber }]);
  const [rxPhotos, setRxPhotos] = useState({});

  useEffect(() => {
    if (user?.role) {
      getDocRep({ MgCd: user.role, HqMstId: user.HqMstId })
        .unwrap()
        .then((r) => setDoctors(r.data || []))
        .catch(console.error);
    }
  }, [user, getDocRep]);

  const setWeekDate = (idx, val) => {
    const updated = [...weekDates];
    updated[idx] = val;
    setWeekDates(updated);
  };

  const updatePrescriberCount = (count) => {
    const n = Math.max(1, Math.min(5, parseInt(count) || 1));
    setPrescriberCount(n);
    const arr = [];
    for (let i = 0; i < n; i++) arr.push(prescribers[i] || { ...emptyPrescriber });
    setPrescribers(arr);
  };

  const updatePrescriber = (idx, field, value) => {
    const updated = [...prescribers];
    updated[idx] = { ...updated[idx], [field]: value };
    setPrescribers(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!weekDates[0]) { toast.error("Week 1 date is required"); return; }
    if (!chemistStockCount) { toast.error("No. of chemist stock available is required"); return; }
    if (!totalStripsAtChemist) { toast.error("Total no. of strips at chemist is required"); return; }
    if (!pobTakenStrips) { toast.error("Total POB taken is required"); return; }

    const weekStartDate = getMondayOf(weekDates[0]) || weekDates[0];

    const weeksData = weekDates.map((d, idx) => {
      if (!d) return null;
      const mon = getMondayOf(d);
      if (!mon) return null;
      const monD = new Date(mon + "T00:00:00");
      const sunD = new Date(monD);
      sunD.setDate(sunD.getDate() + 6);
      return {
        week_number: idx + 1,
        week_start: mon,
        week_end: sunD.toISOString().split("T")[0],
      };
    }).filter(Boolean);

    const fd = new FormData();
    fd.append("EmpNo", user.id);
    fd.append("MgCd", user.role);
    fd.append("weekStartDate", weekStartDate);
    fd.append("chemistStockCount", chemistStockCount);
    fd.append("totalStripsAtChemist", totalStripsAtChemist);
    fd.append("pobTakenStrips", pobTakenStrips);
    fd.append("prescriberCount", prescriberCount);
    fd.append("prescribers", JSON.stringify(
      prescribers.map((p, i) => ({ slot_number: i + 1, doctorName: p.doctorName, doctCd: p.doctCd }))
    ));
    fd.append("weeks", JSON.stringify(weeksData));

    prescribers.forEach((_, i) => {
      if (rxPhotos[i]) fd.append(`rx_dr_${i + 1}`, rxPhotos[i]);
    });

    try {
      await submitWeekly(fd).unwrap();
      toast.success("Weekly tracker submitted!");
      setPrescribers([{ ...emptyPrescriber }]);
      setPrescriberCount(1);
      setChemistStockCount(""); setTotalStripsAtChemist(""); setPobTakenStrips("");
      setWeekDates([today, "", "", ""]);
      setRxPhotos({});
    } catch (err) {
      toast.error(err?.data?.message || "Submit failed");
    }
  };

  return (
    <Box sx={{ maxWidth: 660, mx: "auto" }}>
      <BisberryLogo />

      <form onSubmit={handleSubmit}>
        {/* ── 4 Week Selectors ── */}
        <Grid container spacing={1.5} sx={{ mb: 3 }}>
          {[0, 1, 2, 3].map((idx) => (
            <Grid item xs={6} sm={3} key={idx}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5, borderRadius: 2, borderColor: weekDates[idx] ? "#7B0000" : "#ccc",
                  borderWidth: weekDates[idx] ? 2 : 1,
                }}
              >
                <Typography variant="caption" fontWeight={700} sx={{ color: "#7B0000" }}>
                  {idx === 0 ? "*" : ""}Week {idx + 1}
                </Typography>
                <TextField
                  fullWidth type="date" size="small"
                  value={weekDates[idx]}
                  onChange={(e) => setWeekDate(idx, e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mt: 0.5 }}
                  required={idx === 0}
                />
                {weekDates[idx] && (
                  <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                    {weekRangeLabel(weekDates[idx])}
                  </Typography>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* ── Chemist & POB Fields ── */}
        <TextField
          fullWidth type="number" label="*No. of chemist stock available"
          value={chemistStockCount} onChange={(e) => setChemistStockCount(e.target.value)}
          inputProps={{ min: 0 }} sx={{ mb: 2 }} required
        />
        <TextField
          fullWidth type="number" label="*Total no. of strips available at chemist"
          value={totalStripsAtChemist} onChange={(e) => setTotalStripsAtChemist(e.target.value)}
          inputProps={{ min: 0 }} sx={{ mb: 2 }} required
        />
        <TextField
          fullWidth type="number" label="*Total POB taken (In strips)"
          value={pobTakenStrips} onChange={(e) => setPobTakenStrips(e.target.value)}
          inputProps={{ min: 0 }} sx={{ mb: 2 }} required
        />

        {/* ── Prescriber Count ── */}
        <TextField
          fullWidth type="number" label="*No. of prescribers (1–5)"
          value={prescriberCount}
          onChange={(e) => updatePrescriberCount(e.target.value)}
          inputProps={{ min: 1, max: 5 }} sx={{ mb: 2 }} required
        />

        {/* ── Prescriber Rows — name left, upload right ── */}
        {prescribers.map((p, i) => (
          <Grid container spacing={1.5} key={i} alignItems="center" sx={{ mb: 1.5 }}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={doctors}
                getOptionLabel={(opt) => `${opt.DoctName} (${opt.DoctCd || "N/A"})`}
                onChange={(_, v) => {
                  updatePrescriber(i, "doctorName", v?.DoctName || "");
                  updatePrescriber(i, "doctCd", v?.DoctCd || "");
                }}
                renderInput={(params) => (
                  <TextField {...params} label={`*Name of prescriber ${i + 1}`} size="small" />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CameraUpload
                label={`*Upload Rx Dr.${i + 1}`}
                onFileSelect={(f) => setRxPhotos((prev) => ({ ...prev, [i]: f }))}
              />
            </Grid>
          </Grid>
        ))}

        <Button
          fullWidth variant="contained" type="submit" size="large"
          disabled={isLoading}
          sx={{ mt: 2, py: 1.5, bgcolor: "#7B0000", "&:hover": { bgcolor: "#5a0000" } }}
        >
          {isLoading ? <CircularProgress size={24} color="inherit" /> : "Submit"}
        </Button>
      </form>

      <Typography
        variant="body2"
        sx={{ mt: 3, textAlign: "center", color: "text.secondary" }}
      >
        <Box component="span" sx={{ color: "#C41E3A", fontWeight: 700 }}>Note: </Box>
        Sections marked with{" "}
        <Box component="span" sx={{ color: "#C41E3A", fontWeight: 700 }}>*</Box>
        {" "}are mandatory for the submission
      </Typography>
    </Box>
  );
};

export default WeeklyTracker;
